import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  packageContainerClassValues,
  packageTypeCreateSchema,
  type PackageType,
  type PackageTypeCreateInput,
} from './types';
import { useCreatePackageType, useUpdatePackageType } from './usePackageTypes';

interface PackageTypesFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied the dialog runs in edit mode and PUTs `/package-types/:id`. */
  row?: PackageType | null;
}

export const PackageTypesFormDialog = ({
  open,
  onOpenChange,
  row,
}: PackageTypesFormDialogProps) => {
  const { t } = useTranslation();
  const create = useCreatePackageType();
  const update = useUpdatePackageType();
  const isEdit = !!row;

  const form = useForm<PackageTypeCreateInput>({
    resolver: zodResolver(packageTypeCreateSchema),
    defaultValues: {
      type_code: '',
      display_name: '',
      container_class: 'pallet',
      default_tare_kg: '0.000',
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset({
        type_code: '',
        display_name: '',
        container_class: 'pallet',
        default_tare_kg: '0.000',
      });
      return;
    }
    if (row) {
      form.reset({
        type_code: row.type_code,
        display_name: row.display_name,
        container_class: row.container_class,
        default_tare_kg: row.default_tare_kg,
      });
    }
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (isEdit && row) {
      await update.mutateAsync({ id: row.package_type_id, body: values });
    } else {
      await create.mutateAsync(values);
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || update.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit
              ? t('efs.masters.package_types.edit', { ns: 'masters' })
              : t('efs.masters.package_types.create', { ns: 'masters' })}
          </SheetTitle>
          <SheetDescription>
            {t('efs.masters.package_types.create_subtitle', { ns: 'masters' })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="type_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.package_types.fields.type_code', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.package_types.fields.display_name', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="container_class"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.package_types.fields.container_class', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {packageContainerClassValues.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {t(`efs.masters.package_types.container_classes.${opt}`, {
                              ns: 'masters',
                              defaultValue: opt,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="default_tare_kg"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.package_types.fields.default_tare_kg', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input type="text" inputMode="decimal" placeholder="0.000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.masters.actions.create', { ns: 'masters' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
